import { useRef, useState } from "react";
import { Skeleton, Stack, Tab, Tabs } from "@mui/material";
import { DataGrid, GridPaginationModel, GridRowId, GridRowSelectionModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { SourcesTypes } from "../..";
import SimpleBarChart, { MultiValueData } from "../../../../../../../components/charts/graph/SimpleBarChart";
import TableSelectActions from "../../../../../../../components/common/table/TableData/TableSelectActions";
import PaperCard from "../../../../../../../components/widgets/PaperCard";
import PaperCardHeader from "../../../../../../../components/widgets/PaperCard/PaperCardHeader";
import { ConsumptionPageDTO, ConsumptionPageItemDTO } from "../../../../../../../datasources/api-client/data-contracts";
import { LabeledValue } from "../../../../../../../datasources/responses/resultsOverview";
import { EmissionsTypesColors, ThemeColors } from "../../../../../../../theme/themeValues";
import CustomNoRowsOverlay from "../../../../../ResultsTransport/TransportGreenrouter/components/CustomNoRowsOverlay";
import { consumptionsCols, getConsumptionRows, getConsumptionsColumns } from "../../data";

type Props = {
	selectedData: ConsumptionPageDTO | undefined;
	selectedKey: string;
	sourceKeys: (keyof SourcesTypes)[];
	loading: boolean;
	reload: VoidFunction;
	paginationModel: GridPaginationModel;
	handlePaginationChange: (model: GridPaginationModel) => void;
	handleSelectKeyChange: (newValue: keyof SourcesTypes) => void;
	chartData: MultiValueData[] | LabeledValue[];
	chartColor: EmissionsTypesColors;
	chartMeasure: string;
};

const ConsumptionsAndEmissions = ({
	selectedData,
	selectedKey,
	sourceKeys,
	loading,
	reload,
	paginationModel,
	handlePaginationChange,
	handleSelectKeyChange,
	chartData,
	chartColor,
	chartMeasure,
}: Props) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const { t } = useTranslation("feature.units");

	// Track selected rows and their IDs
	const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>({
		type: "include",
		ids: new Set<GridRowId>(),
	});

	const [selectedRows, setSelectedRows] = useState<ConsumptionPageDTO[]>([]);

	// Handle selection of table rows, filter consumptionsData accordingly
	const handleRowSelection = (newSelection: GridRowSelectionModel) => {
		setSelectedRowIds(newSelection);

		const selectedRow: ConsumptionPageDTO["items"] =
			newSelection.type === "include"
				? (selectedData?.items?.filter((item: ConsumptionPageItemDTO) => newSelection.ids.has(item.id)) ?? [])
				: (selectedData?.items?.filter((item: ConsumptionPageItemDTO) => !newSelection.ids.has(item.id)) ?? []);

		setSelectedRows(selectedRow);
	};

	// Tabs for source selection
	const renderTabs = (
		<Stack spacing={2} direction="column" width="100%" alignItems="flex-start" paddingBottom={2}>
			<Tabs
				value={selectedKey}
				onChange={(_, newValue) => handleSelectKeyChange(newValue)}
				variant="scrollable"
				scrollButtons="auto"
			>
				{sourceKeys.map((key) => (
					<Tab key={key} label={t(`greenrouter/consumption emission/${key}`)} value={key} />
				))}
			</Tabs>
		</Stack>
	);

	// Loading state
	if (loading) {
		return <Skeleton variant="rounded" height="100%" width="100%" />;
	}

	return (
		<PaperCard
			ref={containerRef}
			header={
				<PaperCardHeader
					title={t("greenrouterList/list of Greenrouter operating units")}
					isLoading={loading}
					onReload={reload}
					containerRef={containerRef}
				/>
			}
			content={
				<>
					{/* Source selection tabs */}
					{renderTabs}

					{/* Bar chart */}
					<SimpleBarChart data={chartData} measure={chartMeasure} color={chartColor} isTabMode={true} />

					{/* Table actions when rows are selected */}
					{!loading && selectedData && (
						<TableSelectActions
							selectedRowsId={
								selectedRowIds.type === "include"
									? Array.from(selectedRowIds.ids)
									: (selectedData?.items ?? [])
											.filter((item: ConsumptionPageItemDTO) => !selectedRowIds.ids.has(item.id))
											.map((item: ConsumptionPageItemDTO) => item.id)
							}
						/>
					)}

					{/* Data grid with consumptions */}
					<Stack sx={{ width: "100%", height: 450 }}>
						<DataGrid
							getRowId={(row) => row.id}
							rows={selectedData && getConsumptionRows(selectedData, t)}
							columns={getConsumptionsColumns(consumptionsCols, t)}
							disableColumnMenu
							pagination
							paginationMode="server"
							rowCount={selectedData?.itemsCount || 0}
							pageSizeOptions={[10, 25, 50]}
							paginationModel={paginationModel}
							onPaginationModelChange={handlePaginationChange}
							rowHeight={56}
							hideFooterSelectedRowCount
							checkboxSelection={true}
							rowSelectionModel={selectedRowIds}
							onRowSelectionModelChange={handleRowSelection}
							slots={{ noRowsOverlay: () => <CustomNoRowsOverlay /> }}
							sx={{
								"& .MuiDataGrid-columnHeaders": {
									backgroundColor: ThemeColors.LightPrimary,
								},

								"& .MuiDataGrid-row:nth-of-type(even)": {
									backgroundColor: ThemeColors.LightBackgroundElevation0,
								},
							}}
						/>
					</Stack>
				</>
			}
		/>
	);
};

export default ConsumptionsAndEmissions;
